import {
  Box,
  Center,
  Divider,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Portal,
  Spinner,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { cleanDisplayValue } from '../../../utils/functions/formatters'
import { DotsHorizontal, TagFilled } from '../../../utils/constants/icons'
import LazyLoadAvatar from '../media/LazyLoadedAvatar'
import OptionList from '../selects/OptionList'

interface SelectAsInputProps {
  h?: string
  w?: string
  noImage?: boolean
  isTag?: boolean
  isPdf?: boolean
  fontSize?: string
  value: { id: string; name: string; image?: string | null; color?: string } | null
  onChange: (v: { id: string; name: string; image?: string | null; color?: string } | null) => void
  search: string
  onSearchChange: (v: string) => void
  options: {
    id: string
    name: string
    image?: string | null
    color?: string
    detail?: string
    disabled?: boolean
    disabledReason?: string
  }[]
  isLoading?: boolean
  placeholder: string
  isDisabled?: boolean
  itemName?: string
  isAddLoading?: boolean
  hasAddButton?: boolean
  hasNoValueSet?: boolean
  hasRawValueOption?: boolean
  hasSetValueNull?: boolean
  onAdd?: (name: string) => void
  inputLeftElement?: React.ReactNode
  loadOnClick?: boolean
  textAlign?: 'start' | 'center' | 'right'
  modalHeight?: number
  getAvatar?: (value: { id: string; name: string; image?: string | null; color?: string }) => React.ReactNode
  sx?: object
  bg?: string
  borderColor?: string
  noCloseOnRight?: boolean
  triggerButton?: {
    label: string
    icon?: React.ReactNode
    onClick: () => void
  }
}

const SelectAsInput = ({
  value,
  onChange,
  search,
  onSearchChange,
  options,
  isLoading,
  placeholder,
  h,
  w,
  fontSize,
  isDisabled,
  hasAddButton,
  inputLeftElement,
  hasNoValueSet,
  hasRawValueOption,
  hasSetValueNull,
  onAdd,
  itemName,
  isAddLoading,
  noImage,
  isTag,
  isPdf,
  loadOnClick,
  textAlign = 'start',
  modalHeight = 260,
  getAvatar,
  bg,
  borderColor,
  sx,
  noCloseOnRight,
  triggerButton,
}: SelectAsInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const isMobile = useBreakpointValue({ base: true, md: false }, { ssr: false })
  const [isOpen, setIsOpen] = useState(false)
  const [showAllOptions, setShowAllOptions] = useState(false)
  const [localSearch, setLocalSearch] = useState(search)
  const [menuRect, setMenuRect] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 })
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setLocalSearch(search)
  }, [search])

  const updatePos = () => {
    const r = (inputRef.current ?? containerRef.current)?.getBoundingClientRect()
    if (!r) return
    setMenuRect({ top: r.bottom + 4, left: r.left, width: r.width })
  }

  useLayoutEffect(() => {
    if (!isOpen) return
    updatePos()
    const opts: AddEventListenerOptions | boolean = true
    window.addEventListener('scroll', updatePos, opts)
    window.addEventListener('resize', updatePos)
    return () => {
      window.removeEventListener('scroll', updatePos, opts)
      window.removeEventListener('resize', updatePos)
    }
  }, [isOpen])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearch !== search) {
        onSearchChange(localSearch)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [localSearch, search, onSearchChange])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (target.closest('[data-popover-content]')) {
        return
      }

      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        setShowAllOptions(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const hasValue = Boolean(value && value.name !== '')

  return (
    <Box position="relative" w={w ?? '200px'} ref={containerRef}>
      <InputGroup h={h ?? undefined}>
        {inputLeftElement && !hasValue && (
          <InputLeftElement h="calc(100% - 8px)" w="58px" ml="4px" mt="4px">
            {inputLeftElement}
          </InputLeftElement>
        )}
        {hasValue && value && !noImage && !['NO_VALUE_SET', 'NULL_VALUE'].includes(value.name) && (
          <InputLeftElement h="100%" p="0 8px" w="32px" ml="2px">
            {getAvatar ? (
              getAvatar(value)
            ) : (
              <LazyLoadAvatar name={value.name} w={26} h={26} src={value.image} isImage />
            )}
          </InputLeftElement>
        )}
        {hasValue && value && isPdf && !['NO_VALUE_SET', 'NULL_VALUE'].includes(value.name) && (
          <InputLeftElement h="100%" p="0 8px" w="32px" ml="2px">
            <Box
              w="20px"
              h="20px"
              bgImage={'/icons/pdf.svg'}
              bgSize="contain"
              bgRepeat="no-repeat"
              bgPosition="center"
            />
          </InputLeftElement>
        )}
        {hasValue && value && isTag && !['NO_VALUE_SET', 'NULL_VALUE'].includes(value.name) && (
          <InputLeftElement h="100%" p="0 8px" w="32px" ml="2px">
            <Center
              minW="26px"
              maxW="26px"
              w="26px"
              mr="4px"
              h="26px"
              ml="2px"
              borderRadius="50%"
              bg={value?.color ?? 'neutral.100'}
            >
              <Icon w="12px" h="12px" viewBox="0 0 20 20" fill="none" color="white">
                <TagFilled />
              </Icon>
            </Center>
          </InputLeftElement>
        )}
        {hasValue && value && value.name === 'NO_VALUE_SET' && (
          <InputLeftElement h="100%" p="0 8px" w="32px" ml="2px">
            <Center
              minW="26px"
              maxW="26px"
              w="26px"
              mr="4px"
              h="26px"
              ml="2px"
              borderRadius="50%"
              bg={value?.color ?? 'neutral.100'}
            >
              <Icon w="12px" h="12px" viewBox="0 0 24 24" fill="none" color="white">
                <DotsHorizontal />
              </Icon>
            </Center>
          </InputLeftElement>
        )}
        {hasValue && value && value.name === 'NULL_VALUE' && (
          <InputLeftElement h="100%" p="0 8px" w="32px" ml="2px">
            <Center minW="26px" maxW="26px" w="26px" h="26px" borderRadius="50%" bg="neutral.100">
              <Icon w="12px" h="12px" viewBox="0 0 24 24" fill="none" color="primary.500">
                <DotsHorizontal />
              </Icon>
            </Center>
          </InputLeftElement>
        )}

        <Input
          ref={inputRef}
          value={hasValue && value ? cleanDisplayValue(value.name) : localSearch}
          onChange={(e) => {
            if (!hasValue) {
              setLocalSearch(e.target.value)
              setShowAllOptions(false)
              setIsOpen(true)
            }
          }}
          onFocus={() => {
            if (hasValue) {
              onChange(null)
              onSearchChange('')
              setLocalSearch('')
              setIsOpen(false)
              setShowAllOptions(false)
            } else if (loadOnClick || triggerButton) {
              setIsOpen(true)
            }
          }}
          onClick={() => {
            if (hasValue) {
              onChange(null)
              onSearchChange('')
              setLocalSearch('')
              setIsOpen(false)
              setShowAllOptions(false)
            } else if (loadOnClick || triggerButton) {
              setIsOpen(true)
            }
          }}
          h={h ?? (isMobile ? '44px' : undefined)}
          fontSize={isMobile ? '16px' : (fontSize ?? '14px')}
          placeholder={placeholder}
          pl={
            inputLeftElement && !hasValue
              ? '68px'
              : hasValue && !noImage && value && !['NO_VALUE_SET', 'NULL_VALUE'].includes(value.name)
              ? '40px'
              : undefined
          }
          w={w ?? '200px'}
          disabled={isDisabled}
          readOnly={hasValue}
          bg={bg}
          borderColor={borderColor}
          _hover={{
            borderColor: borderColor
          }}
          _focus={{
            bg: 'white',
            borderColor: borderColor,
            boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.1)',
          }}
          sx={sx}
        />
        {!noCloseOnRight && (
          <InputRightElement
            h={h ?? undefined}
            cursor="pointer"
            onClick={() => {
              if (hasValue) {
                onChange(null)
                onSearchChange('')
                setLocalSearch('')
                setIsOpen(false)
                setShowAllOptions(false)
              } else if (loadOnClick) {
                setIsOpen(!isOpen)
              }
            }}
          >
            {isOpen && isLoading ? (
              <Spinner color="primary.500" size="xs" />
            ) : hasValue ? (
              <Center
                w="18px"
                h="18px"
                borderRadius="full"
                bg="neutral.100"
                _hover={{ bg: 'neutral.200' }}
                transition="background 0.15s"
              >
                <Icon viewBox="0 0 24 24" w="10px" h="10px" color="neutral.500">
                  <path
                    fill="currentColor"
                    d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                  />
                </Icon>
              </Center>
            ) : null}
          </InputRightElement>
        )}
      </InputGroup>
      {isOpen && !isLoading && (localSearch.length > 0 || loadOnClick || showAllOptions || !!triggerButton) && (
        <Portal>
          <Box
            role="listbox"
            position="fixed"
            top={`${menuRect.top}px`}
            left={`${menuRect.left}px`}
            w={`${menuRect.width}px`}
            bg="white"
            py="6px"
            boxShadow="0px 6px 35px 5px #0000001F"
            borderColor="neutral.100"
            borderWidth="1px"
            borderRadius="8px"
            zIndex={9999}
            maxH={`${modalHeight}px`}
            overflowY="auto"
            ref={menuRef}
          >
            {triggerButton && (
              <>
                <Flex
                  px="16px"
                  h="40px"
                  cursor="pointer"
                  alignItems="center"
                  gap="8px"
                  transition="0.15s ease-out"
                  _hover={{ bg: 'primary.50' }}
                  onClick={() => {
                    setIsOpen(false)
                    triggerButton.onClick()
                  }}
                >
                  {triggerButton.icon && (
                    <Center w="24px" h="24px" flexShrink={0}>
                      {triggerButton.icon}
                    </Center>
                  )}
                  <Text textStyle="b-sm" fontWeight="600" color="primary.600" noOfLines={1}>
                    {triggerButton.label}
                  </Text>
                </Flex>
                <Divider m="0" />
              </>
            )}
            <Box
              maxH="calc(254px - 12px)"
              overflowY="auto"
              overflowX="hidden"
              onWheel={(e) => {
                e.stopPropagation()
                const container = e.currentTarget
                container.scrollTop += e.deltaY
              }}
              sx={{
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'neutral.200',
                  borderRadius: '2px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: 'neutral.300',
                },
              }}
            >
              <OptionList
                options={options}
                onOptionSelect={(option: { id: string; name: string; image?: string | null; color?: string }) => {
                  onChange(option)
                  onSearchChange('')
                  setLocalSearch('')
                  setIsOpen(false)
                  setShowAllOptions(false)
                }}
                noImage={noImage}
                isTag={isTag}
                isPdf={isPdf}
                textAlign={textAlign}
                isLoading={isLoading}
                searchValue={localSearch}
                showAllOptions={showAllOptions}
                hasAddButton={hasAddButton}
                onAdd={(name: string) => {
                  if (onAdd) {
                    onAdd(name)
                    onSearchChange('')
                    setLocalSearch('')
                    setShowAllOptions(false)
                  }
                }}
                itemName={itemName}
                isAddLoading={isAddLoading}
                hasRawValueOption={hasRawValueOption}
                hasNoValueSet={hasNoValueSet}
                hasSetValueNull={hasSetValueNull}
                getAvatar={getAvatar}
              />
            </Box>
          </Box>
        </Portal>
      )}
    </Box>
  )
}

export default SelectAsInput
